import { motion } from "framer-motion";
import { Layers, Settings2, SlidersHorizontal } from "lucide-react";
import Reveal from "../Reveal";
import SectionHead from "../SectionHead";

const IMG =
    "https://images.unsplash.com/photo-1646082276009-bb35409086ed?q=80&w=1400&auto=format&fit=crop";

const CARDS = [
    {
        icon: Layers,
        title: "Material",
        items: [
            "Fiberglass reinforced epoxy",
            "GFRP / FRP insulation materials",
            "Customer-specified grades",
        ],
    },
    {
        icon: Settings2,
        title: "Manufacturing",
        items: [
            "Moulding / compression moulding",
            "Cutting",
            "Drilling",
            "Slotting",
            "Trimming",
            "Machining",
            "Edge finishing",
            "Dimensional inspection",
        ],
    },
    {
        icon: SlidersHorizontal,
        title: "Customization",
        items: [
            "Customer-specific dimensions",
            "Wall thickness",
            "Holes & slots",
            "Mounting arrangements",
            "Inserts, where required",
            "Surface finish",
            "Special tolerances as per drawing",
        ],
    },
];

export const EpoxyBox = () => (
    <section id="epoxy-box" className="py-24 md:py-32 scroll-mt-20 bg-panel/40 border-y border-zinc-900">
        <div className="shell">
            <SectionHead
                num="02"
                overline="Signature Capability"
                title="FRP Epoxy Box Manufacturing"
                desc="We undertake manufacturing of FRP/Epoxy Boxes and Enclosures according to customer drawings and technical specifications."
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
                {CARDS.map((card, i) => (
                    <motion.div
                        key={card.title}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: i * 0.1 }}
                        data-testid={`epoxybox-card-${card.title.toLowerCase()}`}
                        className="bg-[#0e0e11] p-6 md:p-8 hover:bg-panel transition-colors duration-300 group"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <card.icon
                                size={26}
                                className="text-safety"
                                strokeWidth={1.5}
                            />
                            <span className="font-mono text-[10px] text-zinc-600 tracking-[0.2em]">
                                /{String(i + 1).padStart(2, "0")}
                            </span>
                        </div>
                        <h3 className="font-display text-2xl font-semibold uppercase tracking-wide text-zinc-50 mb-5 group-hover:text-safety transition-colors">
                            {card.title}
                        </h3>
                        <ul className="space-y-2.5">
                            {card.items.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed"
                                >
                                    <span className="w-1 h-1 bg-safety mt-2 shrink-0" />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-800 border border-t-0 border-zinc-800">
                <div className="bg-[#0e0e11] relative overflow-hidden min-h-[260px] group">
                    <img
                        src={IMG}
                        alt="Component fabrication"
                        className="absolute inset-0 w-full h-full object-cover opacity-70 grayscale-[30%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e11] via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-6">
                        <span className="chip-solid">Machined to drawing</span>
                    </div>
                </div>
                <Reveal className="bg-safety text-charcoal p-6 md:p-8 flex flex-col justify-center">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] mb-4 opacity-80">
                        Specification Note
                    </div>
                    <p className="font-display text-lg md:text-2xl font-medium uppercase leading-snug tracking-tight">
                        Final material grade, dimensions, tolerances and process
                        parameters are manufactured according to the approved
                        customer drawing / specification.
                    </p>
                </Reveal>
            </div>
        </div>
    </section>
);

export default EpoxyBox;